import React, { useState, useEffect } from 'react';
import { Bell, Users, Clock, Wand2, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AINoticeGenerator from '../Notices/AINoticeGenerator';

/**
 * ActivityReminderSection — reusable component for activity-level reminders.
 * Shows: status summary, incomplete employee list, fatigue check, and a button
 * to open the AINoticeGenerator in reminder mode.
 * 
 * Props:
 *   activityType: 'task' | 'survey' | 'compliance' | 'course_item'
 *   activityId: number
 *   activityTitle: string (display name of the activity)
 */
const ActivityReminderSection = ({ activityType, activityId, activityTitle }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [incompleteData, setIncompleteData] = useState(null);
    const [reminderHistory, setReminderHistory] = useState([]);
    const [fatigueData, setFatigueData] = useState(null);
    const [showNoticeGenerator, setShowNoticeGenerator] = useState(false);

    useEffect(() => {
        if (isExpanded && activityId) {
            fetchData();
        }
    }, [isExpanded, activityId, activityType]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [incompleteRes, historyRes, fatigueRes] = await Promise.all([
                api.get(`/reminders/incomplete-employees/${activityType}/${activityId}`, { headers }),
                api.get(`/reminders/history/${activityType}/${activityId}`, { headers }),
                api.get(`/reminders/fatigue-check/${activityType}/${activityId}`, { headers })
            ]);

            setIncompleteData(incompleteRes.data);
            setReminderHistory(historyRes.data.history || []);
            setFatigueData(fatigueRes.data);
        } catch (err) {
            console.error('Failed to load reminder data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenReminderEditor = () => {
        setShowNoticeGenerator(true);
    };

    const handleReminderSuccess = () => {
        setShowNoticeGenerator(false);
        fetchData(); // Refresh data after sending
        toast.success('Reminder sent successfully!');
    };

    if (!activityId) return null;

    const summary = incompleteData?.summary;
    const employees = incompleteData?.employees || [];
    const recentlyReminded = fatigueData?.recentlyRemindedCount || 0;
    const totalIncomplete = summary?.totalIncomplete || 0;

    return (
        <>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-amber-50 rounded-lg">
                            <Bell className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="text-left">
                            <h3 className="font-semibold text-gray-800 text-sm">Activity Reminders</h3>
                            <p className="text-xs text-gray-500">
                                {totalIncomplete > 0
                                    ? `${totalIncomplete} employee${totalIncomplete !== 1 ? 's' : ''} haven't completed`
                                    : 'All employees have completed'}
                                {reminderHistory.length > 0 && ` · ${reminderHistory.length} reminder${reminderHistory.length !== 1 ? 's' : ''} sent`}
                            </p>
                        </div>
                    </div>
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                    <div className="border-t border-gray-100 p-5 space-y-4">
                        {loading ? (
                            <div className="py-6 text-center text-gray-500 text-sm">Loading reminder data...</div>
                        ) : (
                            <>
                                {/* Status Summary */}
                                {summary && (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <div className="text-center p-3 bg-red-50 rounded-lg">
                                            <p className="text-lg font-bold text-red-700">{summary.overdue || 0}</p>
                                            <p className="text-[11px] text-gray-500">Overdue</p>
                                        </div>
                                        <div className="text-center p-3 bg-amber-50 rounded-lg">
                                            <p className="text-lg font-bold text-amber-700">{summary.stalled || 0}</p>
                                            <p className="text-[11px] text-gray-500">Stalled</p>
                                        </div>
                                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                                            <p className="text-lg font-bold text-blue-700">{summary.inProgress || 0}</p>
                                            <p className="text-[11px] text-gray-500">In Progress</p>
                                        </div>
                                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                                            <p className="text-lg font-bold text-gray-700">{summary.notStarted || 0}</p>
                                            <p className="text-[11px] text-gray-500">Not Started</p>
                                        </div>
                                    </div>
                                )}

                                {/* Fatigue Warning */}
                                {recentlyReminded > 0 && (
                                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-700">
                                            <strong>{recentlyReminded}</strong> employee{recentlyReminded !== 1 ? 's were' : ' was'} reminded in the last 24 hours.
                                            Consider waiting before sending another reminder.
                                        </p>
                                    </div>
                                )}

                                {/* Incomplete Employee List */}
                                {employees.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                            Incomplete Employees ({employees.length})
                                        </p>
                                        <div className="max-h-48 overflow-y-auto space-y-1.5 rounded-lg border border-gray-100 p-2">
                                            {employees.map((emp, idx) => (
                                                <div key={emp.employee_id || idx} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                                                            {(emp.first_name || '?')[0]}{(emp.last_name || '?')[0]}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-gray-800 truncate">{emp.first_name} {emp.last_name}</p>
                                                            <p className="text-[11px] text-gray-500">
                                                                {emp.status || 'pending'}
                                                                {emp.is_stalled && <span className="ml-1 text-amber-600">· stalled</span>}
                                                                {emp.reminder_count > 0 && <span className="ml-1 text-gray-400">· {emp.reminder_count} reminder{emp.reminder_count !== 1 ? 's' : ''}</span>}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        {emp.days_overdue > 0 && (
                                                            <span className="text-xs text-red-600 font-medium">{emp.days_overdue}d overdue</span>
                                                        )}
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                const btn = e.currentTarget;
                                                                btn.disabled = true;
                                                                try {
                                                                    await api.post('/reminders/send-quick', {
                                                                        activityType,
                                                                        activityId,
                                                                        activityTitle,
                                                                        employeeId: emp.employee_id
                                                                    });
                                                                    toast.success(`Reminder sent to ${emp.first_name}`);
                                                                    fetchData(); // Refresh
                                                                } catch (err) {
                                                                    console.error('Send reminder error:', err);
                                                                    toast.error('Failed to send reminder');
                                                                } finally {
                                                                    btn.disabled = false;
                                                                }
                                                            }}
                                                            className="p-1.5 rounded-lg hover:bg-amber-50 text-gray-400 hover:text-amber-600 transition-colors disabled:opacity-50"
                                                            title={`Send reminder to ${emp.first_name}`}
                                                        >
                                                            <Send size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Reminder History */}
                                {reminderHistory.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                            Recent Reminders
                                        </p>
                                        <div className="space-y-1.5">
                                            {reminderHistory.slice(0, 3).map((r, idx) => (
                                                <div key={r.id || idx} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <Send className="w-3.5 h-3.5 text-gray-400" />
                                                        <div>
                                                            <p className="text-sm text-gray-700">{r.content_preview || 'Reminder sent'}</p>
                                                            <p className="text-[11px] text-gray-400">
                                                                {r.reminder_type} · {r.recipient_count} recipient{r.recipient_count !== 1 ? 's' : ''}
                                                                {r.sent_by_name && ` · by ${r.sent_by_name}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                                                        {new Date(r.sent_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Draft Smart Reminder — opens AINoticeGenerator in reminder mode */}
                                {totalIncomplete > 0 && (
                                    <div className="pt-2">
                                        <button
                                            onClick={handleOpenReminderEditor}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm shadow-sm"
                                        >
                                            <Wand2 size={16} /> Draft Smart Reminder for {totalIncomplete} Employee{totalIncomplete !== 1 ? 's' : ''}
                                        </button>
                                    </div>
                                )}

                                {/* All complete */}
                                {totalIncomplete === 0 && (
                                    <div className="flex items-center gap-2 py-3 justify-center text-green-600">
                                        <CheckCircle2 className="w-5 h-5" />
                                        <p className="text-sm font-medium">All employees have completed this activity!</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* AINoticeGenerator in Reminder Mode */}
            {showNoticeGenerator && (
                <AINoticeGenerator
                    isOpen={showNoticeGenerator}
                    onClose={() => setShowNoticeGenerator(false)}
                    onSuccess={handleReminderSuccess}
                    phase="active"
                    prefilledData={{
                        reminderMode: true,
                        activityType,
                        activityId,
                        activityTitle,
                        recipientIds: employees.map(e => e.employee_id),
                        recipientCount: totalIncomplete,
                        overdueCount: summary?.overdue || 0,
                        stalledCount: summary?.stalled || 0,
                        reminderNumber: (reminderHistory.length || 0) + 1
                    }}
                />
            )}
        </>
    );
};

export default ActivityReminderSection;
